import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Heart, Loader2, Scale, ShieldCheck } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import Turnstile from "@/components/Turnstile";
import PrivacyNote from "@/components/PrivacyNote";
import { API, CANDIDATE } from "@/data/campaign";

const PRESETS = [10, 25, 50, 100, 250];
const MIN_AMOUNT = 5;
const MAX_AMOUNT = 1200;

const inputCls =
  "w-full border border-pine-700/25 bg-paper px-4 py-3.5 text-base text-ink placeholder:text-ink/40 focus:border-terra-500 focus:outline-none";

const labelCls = "mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60";

const Donate = () => {
  const [preset, setPreset] = useState(25);
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({
    first_name: "",
    last_name: "",
    email: "",
    street: "",
    city: "",
    province: "Ontario",
    postal_code: "",
    phone: "",
  });
  const [attested, setAttested] = useState(false);
  const [sending, setSending] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const tsReset = useRef(null);

  const effectiveAmount = custom !== "" ? Number(custom) : preset;
  const overLimit = effectiveAmount > MAX_AMOUNT;
  const underLimit = custom !== "" && effectiveAmount < MIN_AMOUNT;
  const amountValid = !overLimit && !underLimit && !Number.isNaN(effectiveAmount) && effectiveAmount > 0;
  const donorComplete = ["first_name", "last_name", "email", "street", "city", "province", "postal_code"].every(
    (k) => donor[k].trim() !== ""
  );
  const canSubmit = amountValid && donorComplete && attested && tsToken && !sending;

  const setD = (key) => (e) => setDonor((d) => ({ ...d, [key]: e.target.value }));
  const pickPreset = (v) => {
    setPreset(v);
    setCustom("");
  };

  const donate = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        amount: effectiveAmount,
        origin_url: window.location.origin,
        donor: { ...donor, attestation: attested },
        turnstile_token: tsToken,
      });
      if (window.top !== window.self) {
        window.open(data.checkout_url, "_blank", "noopener,noreferrer");
        toast.info("Secure checkout opened in a new tab.");
        setSending(false);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      tsReset.current?.();
      setTsToken("");
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the secure checkout. Please try again.");
      setSending(false);
    }
  };

  return (
    <main data-testid="donate-page" className="pt-28 lg:pt-32">
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <Overline>Fuel the Campaign</Overline>
            <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Every dollar prints flyers, fuels door-knocking, and{" "}
              <span className="italic text-terra-500">reaches neighbours.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/75 sm:text-lg">
              This campaign runs on residents, not developers. Your contribution — of any size — helps Amin reach every
              street in Ward 11 before election day.
            </p>
            <div className="mt-8 space-y-3 text-sm text-ink/65">
              <p className="flex items-center gap-3">
                <ShieldCheck className="h-4 w-4 shrink-0 text-pine-600" />
                Secure checkout processed by Stripe. Amounts in Canadian dollars.
              </p>
              <p className="flex items-center gap-3">
                <Heart className="h-4 w-4 shrink-0 text-pine-600" />
                Contributions to municipal campaigns are not eligible for tax receipts.
              </p>
              <p className="flex items-center gap-3">
                <Scale className="h-4 w-4 shrink-0 text-pine-600" />
                Only individual Ontario residents may contribute — companies and organizations cannot.
              </p>
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-ink/50">
              Questions? Call {CANDIDATE.phone} or email {CANDIDATE.email}
            </p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="border border-pine-700/15 bg-white/60 p-6 sm:p-10">
              <h2 className="font-serif text-2xl font-medium tracking-tight">Choose an amount</h2>

              <div data-testid="donate-legal-note" className="mt-5 flex gap-3 border border-terra-500/50 bg-terra-100/50 p-4">
                <Scale className="mt-0.5 h-4 w-4 shrink-0 text-terra-600" />
                <p className="text-sm leading-relaxed text-ink/80">
                  The maximum contribution per person is <strong>$1,200 CAD</strong> for this municipal campaign. This
                  includes money, goods, and tickets to fundraising events.
                </p>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3" data-testid="donate-amounts">
                {PRESETS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`donate-amount-${v}`}
                    onClick={() => pickPreset(v)}
                    className={`border px-4 py-5 font-serif text-2xl font-semibold tracking-tight transition-all ${
                      custom === "" && preset === v
                        ? "border-pine-700 bg-pine-700 text-paper"
                        : "border-pine-700/25 text-pine-700 hover:border-terra-500 hover:text-terra-500"
                    }`}
                  >
                    ${v}
                  </button>
                ))}
                <label className="col-span-3 block">
                  <span className={labelCls}>Custom amount (CAD)</span>
                  <input
                    type="number"
                    min={MIN_AMOUNT}
                    max={MAX_AMOUNT}
                    data-testid="donate-custom-amount"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Any amount between $5 and $1,200"
                    className={inputCls}
                  />
                </label>
              </div>

              {overLimit && (
                <p data-testid="donate-amount-error" className="mt-4 flex items-center gap-2 border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  The maximum contribution per person is $1,200 CAD.
                </p>
              )}
              {underLimit && (
                <p data-testid="donate-min-error" className="mt-4 flex items-center gap-2 border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  The minimum donation is $5 CAD.
                </p>
              )}

              <div className="mt-8 border-t border-pine-700/15 pt-6">
                <h3 className="font-serif text-xl font-medium tracking-tight">Your details</h3>
                <p className="mt-1 text-xs leading-relaxed text-ink/55">
                  Required under Ontario municipal election finance rules. Contributions must come from individuals —
                  not companies or organizations.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className={labelCls}>First Name (Legal) *</span>
                    <input required data-testid="donate-first-name" value={donor.first_name} onChange={setD("first_name")} className={inputCls} placeholder="First name" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Last Name (Legal) *</span>
                    <input required data-testid="donate-last-name" value={donor.last_name} onChange={setD("last_name")} className={inputCls} placeholder="Last name" />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Email *</span>
                    <input required type="email" data-testid="donate-email" value={donor.email} onChange={setD("email")} className={inputCls} placeholder="you@email.com" />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Street Address *</span>
                    <input required data-testid="donate-street" value={donor.street} onChange={setD("street")} className={inputCls} placeholder="123 Main St" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>City *</span>
                    <input required data-testid="donate-city" value={donor.city} onChange={setD("city")} className={inputCls} placeholder="Mississauga" />
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className={labelCls}>Province *</span>
                      <input required data-testid="donate-province" value={donor.province} onChange={setD("province")} className={inputCls} />
                    </label>
                    <label className="block">
                      <span className={labelCls}>Postal Code *</span>
                      <input required data-testid="donate-postal" value={donor.postal_code} onChange={setD("postal_code")} className={inputCls} placeholder="L5M 1A1" />
                    </label>
                  </div>
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Phone (optional)</span>
                    <input type="tel" data-testid="donate-phone" value={donor.phone} onChange={setD("phone")} className={inputCls} placeholder="416-555-0100" />
                  </label>
                </div>
                <label className="mt-5 flex cursor-pointer items-start gap-3 border border-pine-700/20 bg-paper p-4">
                  <input
                    type="checkbox"
                    data-testid="donate-attestation"
                    checked={attested}
                    onChange={(e) => setAttested(e.target.checked)}
                    className="mt-1 h-5 w-5 shrink-0 accent-pine-700"
                  />
                  <span className="text-sm leading-relaxed text-ink/80">
                    I am an individual resident of Ontario, this contribution is from my own funds, and I am not
                    prohibited from contributing under Ontario municipal election finance rules.
                  </span>
                </label>
              </div>

              <div className="mt-8">
                <Turnstile action="donate" onToken={setTsToken} resetRef={tsReset} />
              </div>

              <button
                onClick={donate}
                disabled={!canSubmit}
                data-testid="donate-submit-button"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-blue-700 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
                {sending
                  ? "Opening secure checkout…"
                  : amountValid
                    ? `Donate $${effectiveAmount} CAD`
                    : "Enter a valid amount to donate"}
              </button>
              <p className="mt-4 text-center text-xs text-ink/50">
                You’ll be taken to a secure payment page to complete your donation.
              </p>
              <PrivacyNote center />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Donate;
